import React, { useState, useCallback, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  Email,
  Lock,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { THEME_COLORS, BREAKPOINTS } from '../constants';
import logo from '../assets/images/EweAlert.png';

const LoginPageComponent = () => {
  const { login, isAuthenticated, loading: authLoading } = useAuth();
  const isMobile = useMediaQuery(`(max-width:${BREAKPOINTS.MOBILE}px)`);
  
  // Estados del componente
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Validar email (memoizado)
  const isValidEmail = useCallback((email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }, []);

  // Manejar cambios en inputs
  const handleInputChange = useCallback((event) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError(''); // Limpiar errores al escribir
  }, [error]);

  // Manejar envío del formulario
  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    
    const { email, password } = formData;
    
    // Validaciones mejoradas
    if (!email.trim()) {
      setError('Ingresa tu email');
      return;
    }
    
    if (!isValidEmail(email)) {
      setError('Ingresa un email válido');
      return;
    }
    
    if (!password) {
      setError('Ingresa tu contraseña');
      return;
    }
    
    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const success = await login(email, password);
      
      if (!success) {
        setError('Credenciales incorrectas o error del servidor');
      }
      // La redirección se manejará automáticamente por el AuthContext/App.js
    } catch (err) {
      setError(
        err.message.includes('Failed to fetch')
          ? 'Sin conexión al servidor. Verifica la URL y tu conexión.'
          : err.message || 'Error al iniciar sesión'
      );
    } finally {
      setLoading(false);
    }
  }, [formData, isValidEmail, login]);

  // Alternar visibilidad de contraseña
  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  // Verificar si el formulario es válido
  const isFormValid = useMemo(() => {
    return formData.email.trim() && 
           formData.password && 
           isValidEmail(formData.email) && 
           formData.password.length >= 6;
  }, [formData, isValidEmail]);

  // Estilos memoizados para campos
  const fieldStyles = useMemo(() => ({
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
      transition: 'all 0.2s ease-in-out',
      '& fieldset': {
        borderColor: THEME_COLORS.BORDER_LIGHT,
      },
      '&:hover fieldset': {
        borderColor: THEME_COLORS.BORDER_HOVER,
      },
      '&.Mui-focused fieldset': {
        borderColor: THEME_COLORS.PRIMARY,
        borderWidth: '2px',
      },
      '&.Mui-error fieldset': {
        borderColor: '#dc2626',
      },
    },
    '& .MuiInputLabel-root': {
      color: THEME_COLORS.TEXT_SECONDARY,
      '&.Mui-focused': {
        color: THEME_COLORS.PRIMARY,
      },
      '&.Mui-error': {
        color: '#dc2626',
      },
    },
  }), []);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
        backgroundImage: `radial-gradient(circle at 50% 50%, ${THEME_COLORS.PRIMARY}08 0%, transparent 50%)`,
      }}
    >
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >

          {/* Formulario */}
          <Paper
            elevation={0}
            sx={{
              padding: isMobile ? 3 : 4,
              width: '100%',
              maxWidth: 420,
              backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
              border: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
              borderRadius: '20px',
              boxShadow: `0 8px 32px ${THEME_COLORS.SHADOW_LIGHT}`,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
              <img
                src={logo}
                alt="Logo eWeAlert"
                style={{ height: 64, width: 'auto', marginBottom: 16, borderRadius: 16 }}
              />
            </Box>
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                
                {/* Campo Email */}
                <TextField
                  fullWidth
                  required
                  name="email"
                  type="email"
                  label="Correo electrónico"
                  placeholder="usuario@ejemplo.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={loading || authLoading}
                  error={!!error && !formData.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: THEME_COLORS.TEXT_SECONDARY }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldStyles}
                />

                {/* Campo Contraseña */}
                <TextField
                  fullWidth
                  required
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  label="Contraseña"
                  placeholder="Tu contraseña"
                  value={formData.password}
                  onChange={handleInputChange}
                  disabled={loading || authLoading}
                  error={!!error && !!formData.password && formData.password.length < 6}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: THEME_COLORS.TEXT_SECONDARY }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                          onClick={togglePasswordVisibility}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                          disabled={loading || authLoading}
                          sx={{ 
                            color: THEME_COLORS.TEXT_SECONDARY,
                            '&:hover': { 
                              color: THEME_COLORS.TEXT_PRIMARY,
                              backgroundColor: THEME_COLORS.LIGHT_GRAY,
                            }
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldStyles}
                />

                {/* Mensaje de Error */}
                {error && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      borderRadius: '12px',
                      backgroundColor: '#fef2f2',
                      color: '#991b1b',
                      border: '1px solid #fecaca',
                      '& .MuiAlert-icon': {
                        color: '#dc2626',
                      },
                    }}
                  >
                    {error}
                  </Alert>
                )}

                {/* Botón de Login */}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={!isFormValid || loading || authLoading}
                  sx={{
                    height: 56,
                    borderRadius: '12px',
                    backgroundColor: THEME_COLORS.TEXT_PRIMARY,
                    color: THEME_COLORS.BACKGROUND_PAPER,
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: `0 4px 12px ${THEME_COLORS.SHADOW_LIGHT}`,
                    '&:hover': {
                      backgroundColor: '#1a1a1a',
                      transform: 'translateY(-1px)',
                      boxShadow: `0 6px 16px ${THEME_COLORS.SHADOW_LIGHT}`,
                    },
                    '&:disabled': {
                      backgroundColor: THEME_COLORS.LIGHT_GRAY,
                      color: THEME_COLORS.TEXT_MUTED,
                      transform: 'none',
                      boxShadow: 'none',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CircularProgress size={20} sx={{ color: THEME_COLORS.BACKGROUND_PAPER }} />
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        Iniciando sesión...
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LoginIcon sx={{ fontSize: '1.25rem' }} />
                      Iniciar Sesión
                    </Box>
                  )}
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Pie de página */}
          <Typography
            variant="body2"
            sx={{
              color: '#9ca3af',
              textAlign: 'center',
              fontSize: '0.85rem',
              mt: 6,
              mb: 2,
            }}
          >
            © 2025 eWeAlert. Plataforma segura de gestión y notificaciones. Todos los derechos reservados.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

const LoginPage = React.memo(LoginPageComponent);
LoginPage.displayName = 'LoginPage';

export default LoginPage; 